import { Type } from '@angular/core';
import { MatRow } from '@angular/material/table';
import { has } from 'lodash';
import { InputsType, OutputsType } from 'ng-dynamic-component';

import { Breakpoints } from '@lockton-apps/shared/ui/lui';

export enum DynamicTableColumnTypes {
  Default,
  Context,
  Expand,
  Template,
}

export enum DynamicTableSortDirection {
  Asc = 'asc',
  Desc = 'desc',
}

export enum DynamicTableBreakpoints {
  XL = Breakpoints.GT_XL,
  LG = Breakpoints.GT_LG,
  MD = Breakpoints.GT_MD,
  SM = Breakpoints.GT_SM,
  XS = Breakpoints.GT_XS,
  DEFAULT = 'DEFAULT',
}

export interface DynamicTableConfig {
  columns: Array<Partial<DynamicTableColumnConfig> & { id: string }>;
  expansion?: {
    column: DynamicTableColumnConfig;
    expandOnRowClick?: boolean;
  };
  sort: DynamicTableSortDef;
  pagination: DynamicTablePaginationDef;
  selection?: {
    on: boolean;
    compareWith?: (a: any, b: any) => boolean;
  };
  idPath?: string;
}

export interface DynamicTableVariantsDef {
  columns: Record<string, DynamicTableColumnConfig>;
  sort: DynamicTableSortDef;
  pagination: DynamicTablePaginationDef;
}

export interface DynamicTablePaginationDef {
  paginatable: boolean;
  size: number;
  options: number[];
  showEdgeButtons: boolean;
}

export interface DynamicTableSortDef {
  active: string;
  direction: DynamicTableSortDirection;
}

export interface DynamicTableRowDef {
  clickable: boolean;
}

export interface DynamicTableColumnConfig {
  id: string;
  type: DynamicTableColumnTypes;
  label: string | undefined;
  columnType?: string;
  props?: any;
  template?: Type<unknown>;
  layout?: Partial<
    Record<DynamicTableBreakpoints, DynamicTableColumnLayoutConfig>
  >;
  sortable?: boolean;
  classNames?: string;
}

export interface DynamicTableColumnLayoutConfig {
  flex?: string | number;
  order?: number;
  hideLabel?: boolean;
}

export interface DynamicTableCellBaseConfig<
  TValue = string | number | boolean
> {
  value: TValue;
}

export interface TableExpandCell<T> extends DynamicTableCellBaseConfig {
  component: Type<T>;
  inputs?: InputsType;
  outputs?: OutputsType;
}

export interface TableRowExpanded extends MatRow {
  _id: string;
}

export interface DynamicTableContextActionBase {
  id: string;
  label: string;
  icon: string;
  exposed?: boolean;
  hidden?(row: unknown): boolean;
  disabled?(row: unknown): boolean;
}

interface LinkDef {
  path: string | Array<string | number>;
  queryParams?: Record<string, string | boolean | number>;
  fragment?: string;
}

export interface DynamicTableContextLink extends DynamicTableContextActionBase {
  type: 'link';
  linkCreator?(row: unknown): LinkDef;
  link?: LinkDef;
}

export interface DynamicTableContextAction
  extends DynamicTableContextActionBase {
  type: 'action';
}

export interface DynamicTableContextInlineComponent<E = object, D = object>
  extends DynamicTableContextActionBase {
  type: 'inlineComponent';
  inlineComponent: DynamicTableContextActionInlineComponent<E, D>;
}

export type DynamicTableContext<entity = object, additionalData = object> =
  | DynamicTableContextAction
  | DynamicTableContextLink
  | DynamicTableContextInlineComponent<entity, additionalData>;

export const dynamicTableContextGuards = {
  isLink: (context: DynamicTableContext): context is DynamicTableContextLink =>
    !!(context as DynamicTableContextLink).linkCreator,
  isInlineComponent: (
    context: DynamicTableContext
  ): context is DynamicTableContextInlineComponent =>
    has(context, 'inlineComponent'),
};

export interface CustomTableEvent<E> {
  id: string;
  entity: E;
  event: unknown;
}

export interface MapPropArgs<E, D> {
  entity: E;
  additionalData: D;
}

export interface DynamicTableContextActionInlineComponent<E, D> {
  id: string;
  component: Type<unknown>;
  inputsCreator?: (args: MapPropArgs<E, D>) => InputsType;
  inputs?: InputsType;
  outputs?: OutputsType;
}
